// Chat tab — single-pane conversational interface that talks to chat-api.
//
// Behaviour:
//   - One active conversation per session. "New conversation" archives the
//     current one (server-side) and starts fresh. No sidebar / history list.
//   - Streaming assistant text accumulates into a single bubble per turn.
//   - Tool calls are not rendered as raw JSON. When a tool result has an
//     obvious tabular payload (array of objects) we render a real <table>
//     with Copy (TSV) and Download (CSV + JSON) buttons. Otherwise a
//     compact key=value pair view.
//   - Assistant text is stripped of code fences / XML / reasoning tags so
//     the user never sees the model's intermediate scaffolding.
//   - Write tools pause for explicit Confirm / Cancel before running.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  Copy as CopyIcon,
  Download as DownloadIcon,
  Loader2,
  Send,
  Sparkles,
  Tag as TagIcon,
  XCircle
} from "lucide-react";

import type { Message } from "@attendik/types/chat-schemas";

import {
  chatApi,
  streamMessage,
  type ChatSseEvent,
  type ConfirmationResponse
} from "../api/chat.js";
import { ApiError } from "../api/errors.js";
import { ResultChart, pickChart } from "../components/chat/ResultChart.js";
import { Button } from "../components/ui/button.js";
import { useAuth } from "../store/auth.js";

interface PendingConfirmation {
  toolCallId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
}

interface ToolCard {
  kind: "tool";
  toolUseId: string;
  toolName: string;
  result: unknown | null;
  isError: boolean;
  pendingConfirmation?: PendingConfirmation;
}

interface TextBubble {
  kind: "text";
  role: "user" | "assistant";
  text: string;
}

type TranscriptItem = TextBubble | ToolCard;

export function ChatPage(): JSX.Element {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [composer, setComposer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Initial load: find the user's active conversation, or create one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await chatApi.listConversations();
        if (cancelled) return;
        if (list.length === 0) {
          const created = await chatApi.createConversation();
          if (cancelled) return;
          setActiveId(created.id);
        } else {
          setActiveId(list[0]?.id ?? null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load chat.");
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, []);

  // Whenever the active conversation changes, replay its history.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setError(null);
    setTranscript([]);
    (async () => {
      try {
        const convo = await chatApi.getConversation(activeId);
        if (cancelled) return;
        setTranscript(historyToTranscript(convo.messages ?? []));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load conversation.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // Autoscroll on every change.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [transcript]);

  const startNewConversation = useCallback(
    async (composerSeed?: string) => {
      setError(null);
      setBusy(false);
      abortRef.current?.abort();
      try {
        // Server archives the previous one and returns the fresh row.
        const created = await chatApi.createConversation();
        setTranscript([]);
        setActiveId(created.id);
        if (composerSeed) {
          setComposer(composerSeed);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not start a new chat.");
      }
    },
    []
  );

  const startReportProblem = useCallback(() => {
    return startNewConversation(
      "I'd like to report a problem. Here's what I was doing and what went wrong:\n\n"
    );
  }, [startNewConversation]);

  const send = useCallback(
    async (message: string) => {
      if (!activeId) return;
      if (!message.trim()) return;
      setBusy(true);
      setError(null);

      const userBubble: TextBubble = { kind: "text", role: "user", text: message };
      let assistantBubble: TextBubble | null = null;
      const openToolCards = new Map<string, ToolCard>();
      setTranscript((prev) => [...prev, userBubble]);

      const ctrl = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ctrl;

      try {
        const stream = streamMessage(activeId, message, ctrl.signal);
        for await (const evt of stream) {
          if (ctrl.signal.aborted) break;
          handleEvent(evt, (mutator) => {
            setTranscript((prev) => mutator(prev));
          }, {
            getAssistant: () => assistantBubble,
            setAssistant: (b) => {
              assistantBubble = b;
            },
            openToolCards
          });
        }
      } catch (err) {
        if (!ctrl.signal.aborted) {
          const msg =
            err instanceof Error && err.message
              ? err.message
              : "The chat service connection was interrupted.";
          setError(msg);
        }
      } finally {
        setBusy(false);
      }
    },
    [activeId]
  );

  const onConfirm = useCallback(
    async (
      toolCallId: string,
      approved: boolean,
      overrideInput?: Record<string, unknown>
    ) => {
      setError(null);
      try {
        const confirmationResult = await chatApi.confirmToolCall(
          toolCallId,
          approved,
          overrideInput
        );
        setTranscript((prev) => applyConfirmationResult(prev, toolCallId, confirmationResult));
        if (confirmationResult.status === "FAILED") {
          const result = confirmationResult.tool_result as { error?: unknown } | null;
          const msg =
            result && typeof result.error === "string"
              ? result.error
              : "Confirmed action failed.";
          setError(msg);
          return;
        }
        await send(
          approved
            ? "Confirmed. Continue."
            : "I cancelled that action. Please respond accordingly."
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not record confirmation.");
      }
    },
    [send]
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = composer;
      setComposer("");
      void send(text);
    },
    [composer, send]
  );

  return (
    // Full-bleed: AppLayout renders this page without the centred 6xl
    // wrapper. The shell is a column that fills the entire main pane —
    // header, transcript, composer — so the conversation gets every
    // available pixel.
    <div className="flex h-full flex-col bg-background">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-6 py-3">
        <div>
          <h1 className="text-base font-semibold leading-tight">AI Chat</h1>
          <p className="text-xs text-muted-foreground">
            {user?.role === "portal_admin"
              ? "Ask across every customer, or report a problem."
              : "Ask about your events, attendees and operators — or report a problem."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => void startReportProblem()} variant="outline" size="sm">
            <Bug className="mr-2 h-4 w-4" /> Report a problem
          </Button>
          <Button onClick={() => void startNewConversation()} variant="outline" size="sm">
            <Sparkles className="mr-2 h-4 w-4" /> New conversation
          </Button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {transcript.length === 0 ? (
          <EmptyState />
        ) : (
          transcript.map((item, idx) => (
            <TranscriptItemView
              key={idx}
              item={item}
              onConfirm={onConfirm}
              busy={busy}
            />
          ))
        )}
        {busy ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
          </div>
        ) : null}
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="-mt-0.5 mr-1 inline h-4 w-4" />
            {error}
          </div>
        ) : null}
      </div>

      <form
        onSubmit={onSubmit}
        className="flex shrink-0 items-end gap-2 border-t border-border px-6 py-3"
      >
        <textarea
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Ask about an event, find an attendee, or describe a problem…"
          rows={2}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!busy && composer.trim()) {
                onSubmit(e);
              }
            }
          }}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <Button type="submit" disabled={busy || !composer.trim()}>
          <Send className="mr-2 h-4 w-4" /> Send
        </Button>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Text cleaning — strip code fences / XML / reasoning the model emits as
// scaffolding so users see only the answer.
// ----------------------------------------------------------------------------

function cleanAssistantText(raw: string): string {
  return (
    raw
      // Remove fenced code blocks entirely.
      .replace(/```[\s\S]*?```/g, "")
      // Drop XML-style tags including their contents (model's own
      // <thinking>…</thinking> scaffolding etc.).
      .replace(/<([a-zA-Z][\w-]*)\b[^>]*>[\s\S]*?<\/\1>/g, "")
      // Strip any stray self-closing or opening tags.
      .replace(/<\/?[a-zA-Z][\w-]*\b[^>]*\/?>/g, "")
      // Collapse runs of blank lines.
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

// ----------------------------------------------------------------------------
// Transcript / event handling
// ----------------------------------------------------------------------------

function historyToTranscript(messages: Message[]): TranscriptItem[] {
  const items: TranscriptItem[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      const text = textOf(m.content);
      if (text) {
        items.push({ kind: "text", role: "user", text });
      }
    } else if (m.role === "assistant") {
      const text = cleanAssistantText(textOf(m.content));
      if (text) {
        items.push({ kind: "text", role: "assistant", text });
      }
      for (const block of asArray(m.content)) {
        if (block && typeof block === "object" && (block as { type?: string }).type === "tool_use") {
          const tu = block as { id?: string; name?: string };
          items.push({
            kind: "tool",
            toolUseId: tu.id ?? "?",
            toolName: tu.name ?? "(unknown)",
            result: null,
            isError: false
          });
        }
      }
    } else if (m.role === "tool") {
      for (const block of asArray(m.content)) {
        const b = block as {
          type?: string;
          tool_use_id?: string;
          content?: unknown;
          is_error?: boolean;
        };
        if (b.type === "tool_result") {
          for (let i = items.length - 1; i >= 0; i -= 1) {
            const it = items[i];
            if (
              it &&
              it.kind === "tool" &&
              it.toolUseId === b.tool_use_id &&
              it.result === null
            ) {
              it.result = safeParseResult(b.content);
              it.isError = Boolean(b.is_error);
              break;
            }
          }
        }
      }
    }
  }
  return items;
}

function applyConfirmationResult(
  items: TranscriptItem[],
  toolCallId: string,
  confirmation: ConfirmationResponse
): TranscriptItem[] {
  return items.map((item) => {
    if (
      item.kind !== "tool" ||
      item.pendingConfirmation?.toolCallId !== toolCallId
    ) {
      return item;
    }
    return {
      ...item,
      result: confirmation.tool_result,
      isError: confirmation.is_error,
      pendingConfirmation: undefined
    };
  });
}

function asArray(content: unknown): unknown[] {
  return Array.isArray(content) ? content : [];
}

function textOf(content: unknown): string {
  return asArray(content)
    .filter(
      (b): b is { type: "text"; text: string } =>
        Boolean(b) &&
        typeof b === "object" &&
        (b as { type?: string }).type === "text" &&
        typeof (b as { text?: unknown }).text === "string"
    )
    .map((b) => b.text)
    .join("");
}

function safeParseResult(content: unknown): unknown {
  if (typeof content === "string") {
    try {
      return JSON.parse(content);
    } catch {
      return content;
    }
  }
  return content;
}

interface EventHandlerCtx {
  getAssistant(): TextBubble | null;
  setAssistant(b: TextBubble | null): void;
  openToolCards: Map<string, ToolCard>;
}

function handleEvent(
  evt: ChatSseEvent,
  apply: (mutator: (prev: TranscriptItem[]) => TranscriptItem[]) => void,
  ctx: EventHandlerCtx
): void {
  switch (evt.event) {
    case "text_delta": {
      const existing = ctx.getAssistant();
      if (existing) {
        existing.text += evt.data.delta;
        // Live-clean on every delta so the visible text never flashes XML.
        const visible = cleanAssistantText(existing.text);
        const updated: TextBubble = { ...existing, text: visible };
        ctx.setAssistant(updated);
        apply((prev) => prev.map((p) => (p === existing ? updated : p)));
        // Keep our ref pointing at the live (raw) bubble so subsequent
        // deltas keep accumulating; swap the rendered copy each time.
        existing.text = updated.text;
      } else {
        const cleaned = cleanAssistantText(evt.data.delta);
        const fresh: TextBubble = { kind: "text", role: "assistant", text: cleaned };
        ctx.setAssistant(fresh);
        apply((prev) => [...prev, fresh]);
      }
      return;
    }
    case "tool_use_start": {
      const card: ToolCard = {
        kind: "tool",
        toolUseId: evt.data.tool_use_id,
        toolName: evt.data.tool_name,
        result: null,
        isError: false
      };
      ctx.openToolCards.set(evt.data.tool_use_id, card);
      ctx.setAssistant(null);
      apply((prev) => [...prev, card]);
      return;
    }
    case "tool_result": {
      const existing = ctx.openToolCards.get(evt.data.tool_use_id);
      if (existing) {
        existing.result = evt.data.result;
        existing.isError = evt.data.is_error;
        apply((prev) => prev.map((p) => (p === existing ? { ...existing } : p)));
      }
      return;
    }
    case "tool_pending_confirmation": {
      const existing = ctx.openToolCards.get(evt.data.tool_use_id);
      if (existing) {
        existing.pendingConfirmation = {
          toolCallId: evt.data.tool_call_id,
          toolName: evt.data.tool_name,
          toolInput: evt.data.tool_input
        };
        apply((prev) => prev.map((p) => (p === existing ? { ...existing } : p)));
      }
      return;
    }
    case "message_start":
    case "message_end":
      return;
    case "error":
      throw new Error(evt.data.error);
  }
}

// ----------------------------------------------------------------------------
// Subviews
// ----------------------------------------------------------------------------

function EmptyState(): JSX.Element {
  return (
    <div className="space-y-3 py-12 text-center text-sm text-muted-foreground">
      <Sparkles className="mx-auto h-8 w-8" />
      <p>Ask anything about your events, attendees and operators.</p>
      <p className="text-xs">
        Try: <em>"How many people are in the room at the Tech Summit?"</em>{" "}
        or <em>"Something feels broken with Meetpoint sync."</em>
      </p>
    </div>
  );
}

function TranscriptItemView({
  item,
  onConfirm,
  busy
}: {
  item: TranscriptItem;
  onConfirm: (
    toolCallId: string,
    approved: boolean,
    overrideInput?: Record<string, unknown>
  ) => void;
  busy: boolean;
}): JSX.Element | null {
  if (item.kind === "text") {
    // Drop empty assistant bubbles after cleaning.
    if (item.role === "assistant" && !item.text.trim()) {
      return null;
    }
    return (
      <div
        className={
          item.role === "user"
            ? "ml-auto max-w-[70ch] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
            : "mr-auto max-w-[90ch] whitespace-pre-wrap rounded-lg bg-secondary px-3 py-2 text-sm"
        }
      >
        {item.text}
      </div>
    );
  }
  // Tool card — full width so tables can breathe.
  return (
    <div className="mr-auto w-full rounded-md border border-border bg-muted/40 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {item.isError ? (
          <XCircle className="h-3.5 w-3.5 text-destructive" />
        ) : item.result !== null ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        <span>{prettyToolName(item.toolName)}</span>
      </div>
      {item.pendingConfirmation ? (
        <PendingConfirmationView
          confirmation={item.pendingConfirmation}
          onConfirm={onConfirm}
          busy={busy}
        />
      ) : item.result !== null ? (
        <ToolResultView result={item.result} isError={item.isError} toolName={item.toolName} />
      ) : (
        <p className="text-xs text-muted-foreground">Running…</p>
      )}
    </div>
  );
}

function prettyToolName(name: string): string {
  return name.replace(/_/g, " ");
}

function PendingConfirmationView({
  confirmation,
  onConfirm,
  busy
}: {
  confirmation: PendingConfirmation;
  onConfirm: (
    toolCallId: string,
    approved: boolean,
    overrideInput?: Record<string, unknown>
  ) => void;
  busy: boolean;
}): JSX.Element {
  // The file_github_issue tool gets a specialised card so the user can edit
  // title / body / labels before the issue actually files. Other write tools
  // fall back to the generic JSON preview.
  if (confirmation.toolName === "file_github_issue") {
    return (
      <FileIssueConfirmation
        confirmation={confirmation}
        onConfirm={onConfirm}
        busy={busy}
      />
    );
  }

  const [acting, setActing] = useState(false);
  const handle = async (approved: boolean): Promise<void> => {
    setActing(true);
    try {
      await Promise.resolve(onConfirm(confirmation.toolCallId, approved));
    } finally {
      setActing(false);
    }
  };
  return (
    <div className="space-y-2 text-sm">
      <p className="font-medium">Confirm action: {prettyToolName(confirmation.toolName)}</p>
      <KeyValueList data={confirmation.toolInput} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void handle(true)} disabled={acting || busy}>
          Confirm
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handle(false)}
          disabled={acting || busy}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Polished card for the `file_github_issue` write tool. The title is an
 * inline input, the body is a textarea with a preview tab, labels render as
 * removable chips. On Confirm we send an `overrideInput` so the server picks
 * up the edited values and persists them on the audit row.
 */
function FileIssueConfirmation({
  confirmation,
  onConfirm,
  busy
}: {
  confirmation: PendingConfirmation;
  onConfirm: (
    toolCallId: string,
    approved: boolean,
    overrideInput?: Record<string, unknown>
  ) => void;
  busy: boolean;
}): JSX.Element {
  const draft = confirmation.toolInput;
  const initialTitle = typeof draft["title"] === "string" ? (draft["title"] as string) : "";
  const initialBody = typeof draft["body"] === "string" ? (draft["body"] as string) : "";

  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [acting, setActing] = useState(false);

  const confirm = async (): Promise<void> => {
    setActing(true);
    try {
      await Promise.resolve(
        onConfirm(confirmation.toolCallId, true, { title, body })
      );
    } finally {
      setActing(false);
    }
  };
  const cancel = async (): Promise<void> => {
    setActing(true);
    try {
      await Promise.resolve(onConfirm(confirmation.toolCallId, false));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-2 text-sm">
      <p className="flex items-center gap-2 font-medium">
        <Bug className="h-4 w-4" /> File a GitHub issue
      </p>

      <label className="block text-xs font-medium text-muted-foreground">Title</label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Short, specific title"
        maxLength={256}
        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />

      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-muted-foreground">Body (Markdown)</label>
        <div className="flex gap-1 text-xs">
          <button
            type="button"
            onClick={() => setMode("edit")}
            className={
              "rounded px-2 py-0.5 " +
              (mode === "edit" ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60")
            }
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            className={
              "rounded px-2 py-0.5 " +
              (mode === "preview" ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60")
            }
          >
            Preview
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
        />
      ) : (
        <div className="max-h-72 overflow-auto rounded-md border border-border bg-background p-3 text-xs">
          <SimpleMarkdown source={body} />
        </div>
      )}

      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <TagIcon className="h-3 w-3" />
        <span>Labels:</span>
        <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px]">
          customer-report
        </span>
        <span className="ml-1">→ cirilm/attendik-scan</span>
      </p>

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => void confirm()}
          disabled={acting || busy || !title.trim() || body.trim().length < 20}
        >
          File issue
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void cancel()}
          disabled={acting || busy}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Ultra-light Markdown render — enough for the issue preview (headings,
 * bullet lists, inline code, bold/italics, paragraphs). Anything more
 * involved is fine to view raw before filing.
 */
function SimpleMarkdown({ source }: { source: string }): JSX.Element {
  const blocks: JSX.Element[] = [];
  const lines = source.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const text = h[2] ?? "";
      const Tag = (`h${Math.min(level + 2, 6)}` as unknown) as "h3" | "h4" | "h5" | "h6";
      blocks.push(
        <Tag key={i} className="mt-2 font-semibold">
          {renderInline(text)}
        </Tag>
      );
      i += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={i} className="ml-4 list-disc">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>
      );
      continue;
    }
    if (line.trim() === "") {
      i += 1;
      continue;
    }
    const para: string[] = [line];
    i += 1;
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !/^(#{1,3})\s+/.test(lines[i] ?? "") &&
      !/^\s*[-*]\s+/.test(lines[i] ?? "")
    ) {
      para.push(lines[i] ?? "");
      i += 1;
    }
    blocks.push(
      <p key={i} className="my-1">
        {renderInline(para.join(" "))}
      </p>
    );
  }
  return <>{blocks}</>;
}

function renderInline(text: string): JSX.Element {
  // Split on bold/italic/code patterns; keep delimiters in the array.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => {
        if (/^\*\*[^*]+\*\*$/.test(p))
          return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (/^\*[^*]+\*$/.test(p)) return <em key={i}>{p.slice(1, -1)}</em>;
        if (/^`[^`]+`$/.test(p))
          return (
            <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
              {p.slice(1, -1)}
            </code>
          );
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

// ----------------------------------------------------------------------------
// Result rendering — table when tabular, key-value when not.
// ----------------------------------------------------------------------------

interface TabularPick {
  rows: Record<string, unknown>[];
  fieldName: string; // which key in the result holds the array
  scalars: Record<string, unknown>; // remaining top-level scalars
}

function pickTable(result: unknown): TabularPick | null {
  if (!result || typeof result !== "object" || Array.isArray(result)) {
    return null;
  }
  const obj = result as Record<string, unknown>;
  let best: { name: string; rows: Record<string, unknown>[] } | null = null;
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v) && v.length > 0 && v.every((row) => row && typeof row === "object" && !Array.isArray(row))) {
      if (!best || v.length > best.rows.length) {
        best = { name: k, rows: v as Record<string, unknown>[] };
      }
    }
  }
  if (!best) {
    return null;
  }
  const scalars: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k !== best.name && (v === null || typeof v !== "object")) {
      scalars[k] = v;
    }
  }
  return { rows: best.rows, fieldName: best.name, scalars };
}

function ToolResultView({
  result,
  isError,
  toolName
}: {
  result: unknown;
  isError: boolean;
  toolName: string;
}): JSX.Element {
  if (isError) {
    const msg =
      result && typeof result === "object" && "error" in result
        ? String((result as { error: unknown }).error)
        : "Tool failed";
    return <p className="text-sm text-destructive">{msg}</p>;
  }

  const chart = pickChart(toolName, result);
  const tabular = pickTable(result);
  if (tabular || chart) {
    return (
      <div className="space-y-2">
        {tabular && Object.keys(tabular.scalars).length > 0 ? (
          <KeyValueList data={tabular.scalars} compact />
        ) : null}
        {chart ? <ResultChart chart={chart} /> : null}
        {tabular ? (
          <ResultTable rows={tabular.rows} toolName={toolName} field={tabular.fieldName} />
        ) : null}
      </div>
    );
  }

  if (result && typeof result === "object" && !Array.isArray(result)) {
    return <KeyValueList data={result as Record<string, unknown>} />;
  }

  return <p className="text-sm">{String(result)}</p>;
}

function ResultTable({
  rows,
  toolName,
  field
}: {
  rows: Record<string, unknown>[];
  toolName: string;
  field: string;
}): JSX.Element {
  const columns = collectColumns(rows);
  const [copied, setCopied] = useState(false);

  const onCopy = async (): Promise<void> => {
    const tsv = rowsToTsv(rows, columns);
    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard blocked — silently no-op.
    }
  };

  const downloadCsv = (): void => {
    const csv = rowsToCsv(rows, columns);
    triggerDownload(`${toolName}-${field}.csv`, "text/csv;charset=utf-8", csv);
  };

  const downloadJson = (): void => {
    triggerDownload(
      `${toolName}-${field}.json`,
      "application/json",
      JSON.stringify(rows, null, 2)
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {rows.length} {rows.length === 1 ? "row" : "rows"}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => void onCopy()}
            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-secondary"
          >
            <CopyIcon className="h-3 w-3" />
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-secondary"
          >
            <DownloadIcon className="h-3 w-3" /> CSV
          </button>
          <button
            type="button"
            onClick={downloadJson}
            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-secondary"
          >
            <DownloadIcon className="h-3 w-3" /> JSON
          </button>
        </div>
      </div>
      <div className="max-h-[60vh] overflow-auto rounded border border-border bg-background">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-muted text-left">
            <tr>
              {columns.map((c) => (
                <th key={c} className="border-b border-border px-2 py-1 font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="even:bg-muted/30">
                {columns.map((c) => (
                  <td
                    key={c}
                    className="border-b border-border/50 px-2 py-1 align-top"
                  >
                    {formatCell(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KeyValueList({
  data,
  compact = false
}: {
  data: Record<string, unknown>;
  compact?: boolean;
}): JSX.Element {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground">(empty)</p>;
  }
  return (
    <dl
      className={
        compact
          ? "flex flex-wrap gap-x-4 gap-y-0.5 text-xs"
          : "grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs"
      }
    >
      {entries.map(([k, v]) => (
        <div key={k} className={compact ? "flex gap-1" : "contents"}>
          <dt className="font-medium text-muted-foreground">{k}</dt>
          <dd className="break-all">{formatCell(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function collectColumns(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      seen.add(k);
    }
  }
  return [...seen];
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function rowsToTsv(rows: Record<string, unknown>[], columns: string[]): string {
  const header = columns.join("\t");
  const lines = rows.map((r) =>
    columns
      .map((c) => formatCell(r[c]).replace(/\t/g, " ").replace(/\n/g, " "))
      .join("\t")
  );
  return [header, ...lines].join("\n");
}

function rowsToCsv(rows: Record<string, unknown>[], columns: string[]): string {
  const esc = (s: string): string =>
    /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  const header = columns.map((c) => esc(c)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => esc(formatCell(r[c]))).join(",")
  );
  return [header, ...lines].join("\n");
}

function triggerDownload(filename: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Suppress unused-import warning for ApiError — it's used for instanceof
// in some development branches.
void ApiError;
